import React, {useCallback, useState} from 'react';

import AemInlineNotification from '@/components/Aem/AemInlineNotification';
import BackButton from '@/components/BackButton/BackButton';
import {CooldownButton} from '@/components/Button';
import {ChevronLeftIcon} from '@/components/Icons';
import {PageHeader} from '@/components/PageHeader';
import {OneTimePasswordField} from '@/components/ui/OneTimePasswordField';
import appConfig from '@/config/appConfig.typed';
import {OTP_TYPE_CODE, OtpTypeCode} from '@/consts/loginTypes';
import useIsDesktopLayout from '@/hooks/layout/useIsDesktopLayout';
import appContent from '@/services/content/appContent.typed';
import type {InlineNotificationModel} from '@/types/aem/inlineNotificationModel';
import stringUtils from '@/utils/stringUtils';

import LoginLanguageSelect from '../LoginLanguageSelect';

interface OtpInfo {
    identifier: string;
    phoneCountryCode?: string;
    channel: OtpTypeCode;
}

interface OtpCodeViewProps {
    info: OtpInfo;
    onComplete: (params: {code: string}) => Promise<void>;
    onResend?: () => Promise<void>;
    onBack?: () => void;
    isLoginLanguageSelectEnabled?: boolean;
}

interface OtpConfig {
    title: string;
    descriptionHtml: string;
    characterCount: number;
    resendReady: string;
    resendCountdown: string;
}

const DEFAULT_COOLDOWN_SECONDS = 60;

const getOtpConfigForChannel = ({channel, identifier, phoneCountryCode}: OtpInfo): OtpConfig => {
    const {emailOtpLength, phoneOtpLength} = appConfig.getOtpSettings();
    const {
        emailCodeTitle,
        emailCodeDescription,
        resendEmailReady,
        resendEmailCountdown,
        smsCodeTitle,
        smsCodeDescription,
        resendSmsReady,
        resendSmsCountdown,
    } = appContent.getOtpContent();

    const isEmail = channel === OTP_TYPE_CODE.EMAIL;

    const DEFAULT_OTP_LENGTH = 6;
    const characterCount = (isEmail ? emailOtpLength : phoneOtpLength) ?? DEFAULT_OTP_LENGTH;
    const title = isEmail ? emailCodeTitle : smsCodeTitle;
    const descriptionTemplate = isEmail ? emailCodeDescription : smsCodeDescription;
    const resendReady = isEmail ? resendEmailReady : resendSmsReady;
    const resendCountdown = isEmail ? resendEmailCountdown : resendSmsCountdown;

    return {
        title,
        descriptionHtml: stringUtils.formatString(
            descriptionTemplate,
            `${isEmail ? '' : phoneCountryCode}${identifier}`
        ),
        characterCount,
        resendReady,
        resendCountdown,
    };
};

const OtpCodeView = ({info, onComplete, onResend, onBack, isLoginLanguageSelectEnabled}: OtpCodeViewProps) => {
    const isDesktopLayout = useIsDesktopLayout();
    const {retryCodeTimeout} = appConfig.getOtpSettings();
    const cooldownSeconds = retryCodeTimeout ?? DEFAULT_COOLDOWN_SECONDS;

    const {otpVerificationGeneralError} = appContent.getOtpContent();
    const [error, setError] = useState<InlineNotificationModel | null>();
    const [value, setOtpValue] = React.useState<string>('');
    const otpFieldRef = React.useRef<HTMLDivElement>(null);

    const {title, descriptionHtml, characterCount, resendReady, resendCountdown} = getOtpConfigForChannel(info);

    const [isLoading, setIsLoading] = useState(false);
    const [otpFieldKey, setOtpFieldKey] = useState(0);

    const handleResendClick = useCallback((): void => {
        resetOtpField();
        setError(null);

        if (onResend) {
            setIsLoading(true);
            onResend().finally(() => {
                setIsLoading(false);
            });
        }
    }, [onResend]);

    const handleBackClick = useCallback((): void => {
        onBack?.();
    }, [onBack]);

    const resetOtpField = (clearValue = true) => {
        if (clearValue) {
            setOtpValue('');
        }
        setOtpFieldKey((prevKey) => prevKey + 1);
    };

    const handleAutoSubmit = async (value: string): Promise<void> => {
        setIsLoading(true);
        setError(null);

        try {
            await onComplete({code: value});
        } catch {
            resetOtpField(false);
            setError(otpVerificationGeneralError);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpFieldFocus = () => {
        if (!error) {
            return;
        }

        setError(null);
        resetOtpField();

        setTimeout(() => {
            const firstInput = otpFieldRef.current?.querySelector('input');

            firstInput?.focus();
        }, 0);
    };

    const handleValueChange = (nextValue: string) => {
        setOtpValue(nextValue);
    };

    return (
        <>
            <PageHeader
                className='pb-4'
                title={title}
                subtitle={<span dangerouslySetInnerHTML={{__html: descriptionHtml}} />}
                startIcon={
                    <BackButton
                        // @ts-ignore
                        icon={<ChevronLeftIcon className='rtl:rotate-180' />}
                        onClick={handleBackClick}
                        className='-left-3 ml:left-0'
                        disabled={isLoading}
                    />
                }
                endIcon={isDesktopLayout && isLoginLanguageSelectEnabled && <LoginLanguageSelect />}
            />
            {error && <AemInlineNotification aria-live='assertive' className='mt-4' model={error} />}
            <div className='mt-8 flex flex-col gap-8 ml:mt-6 ml:gap-6'>
                <OneTimePasswordField
                    key={otpFieldKey}
                    ref={otpFieldRef}
                    value={value}
                    onValueChange={handleValueChange}
                    onFocus={handleOtpFieldFocus}
                    characterCount={characterCount}
                    autoSubmit
                    onAutoSubmit={handleAutoSubmit}
                    disabled={isLoading}
                    hasError={!!error}
                />
                <CooldownButton
                    cooldownSeconds={cooldownSeconds}
                    startInCooldown
                    tertiaryNoActive
                    runningLabel={resendCountdown}
                    label={resendReady}
                    onClick={handleResendClick}
                    disabled={isLoading}
                    loading={isLoading}
                />
            </div>
        </>
    );
};

export default OtpCodeView;
